import { apiClient } from '@/api/client';
import { FilterBar } from '@/components/ui/FilterBar';
import { useAuth } from '@/lib/auth';
import { queryKeys } from '@/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

export interface BusinessesFiltersProps {
  searchTerm: string;
  selectedStatus: string;
  selectedCategory: string;
  selectedCountry: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onClearFilters: () => void;
}

export const BusinessesFilters: React.FC<BusinessesFiltersProps> = ({
  searchTerm,
  selectedStatus,
  selectedCategory,
  selectedCountry,
  onSearchChange,
  onStatusChange,
  onCategoryChange,
  onCountryChange,
  onClearFilters,
}) => {
  const { isAuthenticated } = useAuth();

  const { data: categories } = useQuery({
    queryKey: [queryKeys.ADMIN_CATEGORIES],
    queryFn: async () => {
      const response = await apiClient.GET('/category');
      if (response.error) throw new Error('Failed to fetch categories');
      return response.data || [];
    },
    enabled: isAuthenticated,
  });

  const { data: businesses } = useQuery({
    queryKey: [queryKeys.ADMIN_BUSINESSES, 'countries'],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/businesses', {
        params: { query: { limit: 100 } },
      });
      if (response.error) throw new Error('Failed to fetch businesses');
      return response.data || [];
    },
    enabled: isAuthenticated,
  });

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...(categories?.map(c => ({
      value: c.id?.toString() || '',
      label: c.name || '',
    })) || []),
  ];

  const countryOptions = React.useMemo(() => {
    const countries = new Set<string>();
    businesses?.forEach(b => {
      if (b.country) countries.add(b.country);
    });
    return [
      { value: '', label: 'All Countries' },
      ...Array.from(countries)
        .sort()
        .map(country => ({ value: country, label: country })),
    ];
  }, [businesses]);

  const getStatusLabel = (status: string) =>
    STATUS_OPTIONS.find(opt => opt.value === status)?.label || status;

  const getCategoryLabel = (categoryId: string) => {
    if (!categoryId) return '';
    return categories?.find(c => c.id?.toString() === categoryId)?.name || '';
  };

  return (
    <FilterBar
      searchPlaceholder='Search businesses by name...'
      searchValue={searchTerm}
      onSearchChange={onSearchChange}
      filters={[
        { key: 'status', label: 'Status', options: STATUS_OPTIONS },
        { key: 'category', label: 'Category', options: categoryOptions },
        { key: 'country', label: 'Country', options: countryOptions },
      ]}
      filterValues={{
        status: selectedStatus,
        category: selectedCategory,
        country: selectedCountry,
      }}
      onFilterChange={(key, value) => {
        if (key === 'status') onStatusChange(value);
        if (key === 'category') onCategoryChange(value);
        if (key === 'country') onCountryChange(value);
      }}
      onClearAll={onClearFilters}
      activeFilters={[
        { label: 'Search', value: searchTerm, color: 'blue' },
        {
          label: 'Status',
          value: selectedStatus ? getStatusLabel(selectedStatus) : '',
          color: 'green',
        },
        {
          label: 'Category',
          value: getCategoryLabel(selectedCategory),
          color: 'purple',
        },
        {
          label: 'Country',
          value: selectedCountry,
          color: 'yellow',
        },
      ]}
    />
  );
};
